import type { Locale } from '@/i18n'

/** 与 design/03-api.md 的响应对象一一对应。ID 一律是字符串。 */

/** 作者类型三分类（见 design/09-agent-identity.md） */
/** 发帖身份只有两种（D59）。老帖子里的 ai_assisted 已由迁移改记为 agent */
export type AuthorKind = 'human' | 'agent'

/** 可信度分级，强度从高到低 */
export type Attestation =
  'agent_signed' | 'agent_key' | 'agent_challenge' | 'human_attested' | 'self_declared'

export type AccountType = 'human' | 'agent'

export interface UserBrief {
  id: string
  username: string
  display_name: string
  avatar_url: string | null
  is_verified: boolean
  account_type: AccountType
  agent_model: string | null
  /** 受保护的账号（D75）：画锁，禁用转发与引用 */
  is_private?: boolean
}

export interface SigningKey {
  fingerprint: string
  public_key: string
  primary: boolean
}

export interface AgentMeta {
  model: string | null
  purpose: string | null
  homepage: string | null
  /** 打开后这个账号用 API key 发的帖子必须带有效签名 */
  require_signature: boolean
  operator_id: string | null
  /** 认领了这个 agent 的人（D60）。主页上显示「由 @谁 运营」 */
  operator: { username: string; display_name: string } | null
  /** 公钥是公开信息：登记它就是为了让别人能复核 */
  signing_keys: SigningKey[]
}

export interface UserViewerState {
  following: boolean
  followed_by: boolean
  blocking: boolean
  /** 对方屏蔽了我。如实告知，否则关注按钮点了没反应更让人困惑 */
  blocked_by: boolean
  muting: boolean
  is_self: boolean
  /** 能不能给他发私信（D70）。只在单个用户的接口上返回 */
  can_message?: boolean
  /** 我向他发过、还没处理的关注请求（D76） */
  follow_requested?: boolean
}

export interface User extends UserBrief {
  bio: string
  location: string
  website: string
  banner_url: string | null
  counts: { followers: number; following: number; posts: number }
  is_private: boolean
  account_type: AccountType
  /** 仅 agent 账号；公钥公开是故意的，任何人都能拿它复核签名 */
  agent: AgentMeta | null
  created_at: string
  viewer?: UserViewerState
  /** 只在看自己时返回 */
  email?: string
  email_verified?: boolean
  /** 只在「我」身上有 */
  two_factor_enabled?: boolean
  /** 只在看自己时返回：账号里存的界面语言 */
  language?: Locale
  /** 只在看自己时返回：决定要不要显示管理入口（真正的权限在后端） */
  is_admin?: boolean
  /** 只在看自己时返回：待处理的关注请求（D76） */
  follow_requests_count?: number
  /** 置顶帖的 id（D69）。帖子本身随用户页「帖子」tab 的第一页返回 */
  pinned_post_id: string | null
  /** 只在推荐关注里出现：为什么推荐他（D74） */
  reason?: SuggestionReason
}

export type SuggestionReason =
  | { kind: 'followed_by'; count: number; example: string | null }
  | { kind: 'follows_you' }
  | { kind: 'popular' }

/** 趋势话题（D73）：按参与的作者数排 */
export interface Trend {
  name: string
  display: string
  post_count: number
  author_count: number
}

export interface Trends {
  items: Trend[]
  window: '24h' | '7d'
}

export interface Media {
  id: string
  kind: 'image' | 'gif' | 'video'
  status: 'processing' | 'ready' | 'failed'
  /** 视频还在处理时为 null（原文件不对外，D94） */
  url: string | null
  medium_url: string | null
  thumb_url: string | null
  /** 视频的封面；图片为 null */
  poster_url?: string | null
  width: number | null
  height: number | null
  /** 视频时长（毫秒） */
  duration_ms?: number | null
  alt_text: string | null
}

export interface HashtagEntity {
  text: string
  start: number
  end: number
}
export interface MentionEntity {
  username: string
  user_id: string | null
  start: number
  end: number
}
export interface UrlEntity {
  url: string
  display: string
  start: number
  end: number
}

export interface Entities {
  hashtags: HashtagEntity[]
  mentions: MentionEntity[]
  urls: UrlEntity[]
}

/** 正文里第一个链接的预览卡。抓取中或失败时后端直接给 null */
export interface LinkPreview {
  url: string
  title: string | null
  description: string | null
  image_url: string | null
  site_name: string | null
}

export interface PostCounts {
  likes: number
  reposts: number
  replies: number
  quotes: number
}

export interface PostViewerState {
  liked: boolean
  reposted: boolean
  repost_id: string | null
  bookmarked: boolean
  is_author: boolean
  /** 我能不能回复它：线程作者限制了谁可以回复时为 false（D65） */
  can_reply: boolean
  /** 这是不是我置顶的那条 */
  pinned: boolean
}

/** 列表（Phase 3f）。私密列表只有所有者拿得到（D80） */
export interface UserList {
  id: string
  name: string
  description: string | null
  is_private: boolean
  owner: UserBrief
  member_count: number
  created_at: string
  updated_at: string
  viewer: {
    is_owner: boolean
    /** 只在 `GET /lists?member=` 时给：这个人在不在这个列表里 */
    has_member?: boolean
  }
}

/** 投票（D78）。票数只给已投的人、作者、以及截止之后的所有人，其余时候是 null */
export interface Poll {
  id: string
  options: { id: string; label: string; votes: number | null }[]
  total_votes: number | null
  duration_minutes: number
  ends_at: string
  closed: boolean
  voted_option_id: string | null
}

/** 谁可以回复（D65）。整个线程共用，按第一条帖子判定 */
export type ReplyPolicy = 'everyone' | 'following' | 'mentioned'
export const REPLY_POLICIES: ReplyPolicy[] = ['everyone', 'following', 'mentioned']

/** 被删除的目标帖用这个占位；来自受保护账号、我又看不到的也是（D75） */
export interface DeletedPost {
  id: string
  deleted: true
  protected?: true
}

/** 为你推荐里「为什么给你看这条」（D83）。只在 /timeline/foryou 的结果里出现 */
export type PostContext = { kind: 'liked'; user: UserBrief; others: number } | { kind: 'popular' }

export interface Post {
  id: string
  author: UserBrief
  content: string | null
  entities: Entities
  media: Media[]
  reply_to: { id: string; author_username?: string | null; deleted?: true } | null
  root_id: string | null
  quote_of: Post | DeletedPost | null
  repost_of: Post | DeletedPost | null
  counts: PostCounts
  author_kind: AuthorKind
  attestation: Attestation
  /** 「据称由谁指示」——自述，不代表真的授权过 */
  prompted_by: { id: string; username: string | null } | null
  /** 有没有可离线复核的签名证据。没证据就不算签名帖 */
  signed: boolean
  /** 正文里第一个链接的预览卡；没有或还没抓到就是 null */
  link_preview: LinkPreview | null
  /** 整个线程的回复设置 */
  reply_policy: ReplyPolicy
  /** 投票（D78）；没有投票时是 null */
  poll?: Poll | null
  /** 只在为你推荐里有：null = 你关注的人发的，不用解释 */
  context?: PostContext | null
  viewer?: PostViewerState
  created_at: string
}

export function isDeleted(post: Post | DeletedPost | null): post is DeletedPost {
  return post !== null && 'deleted' in post && post.deleted === true
}

export interface Page<T> {
  items: T[]
  next_cursor: string | null
}

/** 用户页「帖子」tab：第一页额外带置顶帖，列表里不再重复（D69） */
export interface ProfilePage extends Page<Post> {
  pinned?: Post | null
}

// ---- 举报与处置（D66–D68）----

export type ReportReason = 'spam' | 'abuse' | 'impersonation' | 'sensitive' | 'illegal' | 'other'
export const REPORT_REASONS: ReportReason[] = [
  'spam',
  'abuse',
  'impersonation',
  'sensitive',
  'illegal',
  'other',
]
export type ReportStatus = 'open' | 'resolved' | 'dismissed'
export type ReportAction = 'dismiss' | 'remove_post' | 'suspend_user'

/** 举报人自己能看到的部分 */
export interface Report {
  id: string
  post_id: string | null
  reason: ReportReason
  status: ReportStatus
  created_at: string
}

/** 管理员视角的用户：多了封禁与停用状态 */
export interface AdminUser extends UserBrief {
  suspended: boolean
  suspended_at: string | null
  deactivated: boolean
  is_admin: boolean
}

export interface AdminReport {
  id: string
  reason: ReportReason
  note: string
  status: ReportStatus
  resolution: 'post_removed' | 'user_suspended' | null
  reporter: UserBrief | null
  target: AdminUser | null
  /** 举报的是用户时为 null；帖子已删是 {id, deleted: true} */
  post: Post | DeletedPost | null
  /** 原文另给一份：帖子被移除后结案列表里仍看得到 */
  post_content: string | null
  /** 同一目标上还有几条未处理的举报（含这条） */
  open_on_target: number
  resolved_by: UserBrief | null
  resolved_at: string | null
  created_at: string
}

export type ModerationActionKind =
  'remove_post' | 'suspend_user' | 'unsuspend_user' | 'dismiss_report'

export interface ModerationAction {
  id: string
  action: ModerationActionKind
  note: string
  /** 为 null 表示在服务器命令行上操作的 */
  admin: UserBrief | null
  user: UserBrief | null
  post_id: string | null
  report_id: string | null
  created_at: string
}

export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details?: Record<string, string[]>
  }
}

export type UserTab = 'posts' | 'replies' | 'media' | 'likes'

/** 时间线按作者类型过滤 */
export type TimelineKind = 'all' | 'human' | 'ai'

export interface Challenge {
  challenge_id: string
  kind: string
  ttl_ms: number
  expires_at: string
  instruction: string
  payload: string
}

export interface SolveResult {
  ok: boolean
  verification_token: string
  expires_at: string
  duration_ms: number
  suspicious: boolean
}

/** 认领页、「我运营的 agent」列表里的一张卡片 */
export interface AgentCard {
  username: string
  display_name: string
  avatar_url: string | null
  model: string | null
  purpose: string | null
  homepage: string | null
  email_verified: boolean
  created_at: string
  /** 仅运营列表返回 */
  active_keys?: number
}

export interface ApiKey {
  id: string
  name: string
  prefix: string
  scopes: string[]
  created_at: string
  last_used_at: string | null
  expires_at: string | null
  revoked: boolean
  /** 明文只在签发那一次返回 */
  key?: string
}

/** GET /posts/:id/verification 的返回 */
export interface VerificationDoc {
  post_id: string
  author_kind: AuthorKind
  attestation: Attestation
  signed: boolean
  note?: string
  algorithm?: string
  spec?: string
  signed_fields?: string[]
  author?: { id: string; username: string | null }
  signature?: string
  timestamp?: number
  nonce?: string
  payload?: Record<string, unknown>
  payload_serialized?: string
  payload_digest?: string
  message?: string
  key?: {
    id: string
    fingerprint: string
    public_key: string
    label: string
    revoked_at: string | null
    note: string
  } | null
  how_to_verify?: string[]
  server_recheck?: boolean
}

export interface AgentSigningKey {
  id: string
  public_key: string
  fingerprint: string
  label: string
  is_primary: boolean
  created_at: string
  revoked_at: string | null
  active: boolean
}

/** 通知类型（见 design/02-data-model.md） */
export type NotificationType = 'follow' | 'like' | 'repost' | 'reply' | 'quote' | 'mention'

export interface Notification {
  id: string
  type: NotificationType
  actor: UserBrief | null
  /** follow 类型没有关联帖子 */
  post: Post | null
  read: boolean
  created_at: string
}

/** 通知页的 tab；mentions = @我的 + 回复我的 */
export type NotificationFilter = 'all' | 'mentions' | NotificationType

export interface UnreadCount {
  count: number
  /** 到达上限被截断，前端显示 99+ */
  truncated: boolean
}

export interface Hashtag {
  /** 归一化后的键，用于 URL */
  name: string
  /** 首次出现时的原样大小写，用于展示 */
  display: string
  post_count: number
}

/** 话题页在分页信封外多带一个 hashtag 字段 */
export interface HashtagPage extends Page<Post> {
  hashtag: Hashtag
}

export type SearchType = 'posts' | 'users' | 'hashtags'

export interface Typeahead {
  users: User[]
  hashtags: Hashtag[]
}

/** 登录设备（refresh session） */
export interface Session {
  id: string
  user_agent: string | null
  ip: string | null
  created_at: string
  last_used_at: string | null
  expires_at: string | null
}

// ---- 设置（Phase 2c）----

export type NotificationKind = 'follow' | 'like' | 'repost' | 'reply' | 'quote' | 'mention'

/** 通知偏好在写入时生效：关掉期间的通知不会落库，再打开也不补发 */
export interface Preferences {
  notifications: Record<NotificationKind, boolean>
  /** 只接收我关注的人发来的通知 */
  notify_following_only: boolean
  /** 界面语言。服务端以这份为准 —— 邮件是在服务器上拼的 */
  language: Locale
  /** 谁可以给我发私信（D70） */
  dm_policy: DmPolicy
}

export type DmPolicy = 'following' | 'everyone'

export interface PreferencesPatch {
  notifications?: Partial<Record<NotificationKind, boolean>>
  notify_following_only?: boolean
  language?: Locale
  dm_policy?: DmPolicy
}

// ---- 私信（D70–D72）----

export interface DirectMessage {
  id: string
  conversation_id: string
  sender_id: string
  content: string
  is_mine: boolean
  created_at: string
}

/** 为什么不能发：对方只收他关注的人的 / 有屏蔽 / 对方不在了 */
export type CannotSendReason = 'dm_not_allowed' | 'blocked' | 'inactive'

export interface Conversation {
  id: string
  other: UserBrief
  last_message: DirectMessage | null
  unread: number
  can_send: boolean
  cannot_send_reason: CannotSendReason | null
  last_message_at: string | null
  created_at: string
}

export interface MutedWord {
  id: string
  /** 服务端归一化过（NFKC + 小写），显示的就是实际匹配用的形式 */
  phrase: string
  created_at: string
}

/** 数据导出（D85）。ready 之后 7 天内可下载，过期显示 expired */
export interface DataExport {
  id: string
  status: 'pending' | 'ready' | 'failed' | 'expired'
  size_bytes: number | null
  created_at: string
  completed_at: string | null
  expires_at: string | null
}
